import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, Video, Rocket, Globe2, CheckCircle2 } from 'lucide-react';

interface DemoRequestModalProps {
  open: boolean;
  onClose: () => void;
}

export const DemoRequestModal = ({ open, onClose }: DemoRequestModalProps) => {
  const [submitted, setSubmitted] = useState(false);
  
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-card border-border text-foreground">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-6 h-6 text-primary" />
            <DialogTitle className="text-xl font-bold text-gradient">
              Accelerate Your AI Adoption
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            Book a personalized demo to see how chatMCP can transform your MCP infrastructure.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-10 space-y-4 text-center">
            <div className="rounded-full bg-primary/20 p-4">
              <CheckCircle2 className="w-12 h-12 text-primary" />
            </div>
            <h3 className="text-xl font-bold">Request Sent!</h3>
            <p className="text-muted-foreground max-w-xs">
              Our team will be in touch shortly to schedule your demo.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-muted/50 rounded-xl p-4 border border-border/50">
              <h4 className="font-semibold text-primary mb-2 text-sm uppercase tracking-wider">What you'll get</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-foreground">
                  <Video className="w-4 h-4 mr-2 text-primary" />
                  Live walkthrough of the platform
                </li>
                <li className="flex items-center text-sm text-foreground">
                  <Globe2 className="w-4 h-4 mr-2 text-primary" />
                  Architecture review & best practices
                </li>
                <li className="flex items-center text-sm text-foreground">
                  <Calendar className="w-4 h-4 mr-2 text-primary" />
                  Q&A session with an MCP Architect
                </li>
              </ul>
            </div>

            <form
              method="POST"
              action="#" 
              onSubmit={handleSubmit} 
              id="chatMCP_DemoRequestForm"
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="fullname">Full Name *</Label>
                <Input 
                  id="fullname" 
                  name="fullname" 
                  className="bg-background border-border focus:border-primary" 
                  required 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email *</Label>
                  <Input 
                    id="email" 
                    name="email" 
                    type="email" 
                    className="bg-background border-border focus:border-primary" 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone" 
                    name="phone" 
                    type="tel" 
                    className="bg-background border-border focus:border-primary" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input 
                  id="company" 
                  name="company" 
                  className="bg-background border-border focus:border-primary" 
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 rounded-lg shadow-lg"
              >
                Schedule Demo
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
